/* eslint-disable jira/integration/test-id-by-folder-structure */
import React, { memo, useCallback, useState } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { IconButton } from '@atlaskit/button/new';
import { DropdownItemGroup } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/core/migration/show-more-horizontal--more';
import Popup, { type TriggerProps } from '@atlaskit/popup'; // ignore-for-ENGHEALTH-17759
import { Box, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { Tokens, Variables } from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	DROPDOWN,
	FireScreenAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { ThemedButton } from '@atlassian/jira-project-theme-components/src/ui/themed-button/ThemedButton.tsx';
import type { JiraDeleteNavigationItemInput } from '@atlassian/jira-relay/src/__generated__/servicesHideNavigationItemMutation.graphql';
import type {
	ui_navigationKitTabMenuOld_TabMenu$data,
	ui_navigationKitTabMenuOld_TabMenu$key,
} from '@atlassian/jira-relay/src/__generated__/ui_navigationKitTabMenuOld_TabMenu.graphql';
import HideItem from './hide-item/index.tsx';
import messages from './messages.tsx';
import MoveItem from './move-item/index.tsx';
import SetAsDefaultMenuItem from './set-as-default-item/index.tsx';

export type TabMenuProps = {
	tabFragment: ui_navigationKitTabMenuOld_TabMenu$key;
	onHideClick?: (args: {
		id: JiraDeleteNavigationItemInput['id'] | null;
		relayId: ui_navigationKitTabMenuOld_TabMenu$data['__id'] | null;
	}) => void;
	onSetAsDefaultCompleted?: () => void;
	// id of the tab that appears before the tab being rendered, for sorting purposes. Will be `null` when it's the first tab.
	beforeItemId?: string | null;
	// id of the tab that appears after the tab being rendered, for sorting purposes. Will be `null` when it's the last tab.
	afterItemId?: string | null;
	position?: number;
	canEdit?: boolean;
	// whether the menu is attached to an overflowing tab
	isOverflowing?: boolean;
};

const TabMenu = ({
	tabFragment,
	onHideClick,
	onSetAsDefaultCompleted,
	beforeItemId,
	afterItemId,
	position,
	canEdit = false,
	isOverflowing,
}: TabMenuProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [isOpenOld, setIsOpenOld] = useState(false);

	const tabData = useFragment(
		graphql`
			fragment ui_navigationKitTabMenuOld_TabMenu on JiraNavigationItem {
				...setAsDefaultItem_navigationKitTabMenuOld
				id
				__id
				typeKey @required(action: THROW)
				isDefault @required(action: THROW)
				canRemove
				canSetAsDefault
			}
		`,
		tabFragment,
	);
	const canRemove = Boolean(tabData?.canRemove);
	const canSetAsDefault = Boolean(tabData?.canSetAsDefault);

	const handleTriggerClick = useCallback(() => {
		if (!isOpenOld) {
			fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'viewOptionsMenu');
		}
		setIsOpenOld((previousIsOpen) => !previousIsOpen);
	}, [createAnalyticsEvent, isOpenOld]);

	const handleHideClick = useCallback(() => {
		onHideClick?.({
			id: tabData.id,
			relayId: tabData.__id,
		});
	}, [onHideClick, tabData.id, tabData.__id]);

	const handleSetAsDefault = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'setAsDefaultViewOption', {
			viewType: tabData.typeKey.toLowerCase(),
		});
	}, [createAnalyticsEvent, tabData.typeKey]);

	const handleMenuClose = useCallback(() => setIsOpenOld(false), []);

	if (!canRemove && !canSetAsDefault && !canEdit) {
		return null;
	}

	const trigger = (triggerProps: TriggerProps) => (
		<>
			{isOverflowing ? (
				<IconButton
					{...triggerProps}
					label={formatMessage(messages.viewOptions)}
					icon={MoreIcon}
					isTooltipDisabled={false}
					tooltip={{ hideTooltipOnClick: true }}
					onClick={handleTriggerClick}
					appearance="subtle"
					spacing="compact"
					isSelected={undefined}
				/>
			) : (
				<NavigationThemedButton
					{...triggerProps}
					isSelected={undefined}
					onClick={handleTriggerClick}
					appearance="subtle"
					spacing="compact"
					iconBefore={
						<Tooltip content={formatMessage(messages.viewOptions)} hideTooltipOnClick>
							<MoreIcon label={formatMessage(messages.viewOptions)} LEGACY_size="small" />
						</Tooltip>
					}
				/>
			)}
		</>
	);

	return (
		<Popup
			trigger={trigger}
			isOpen={isOpenOld}
			onClose={handleMenuClose}
			placement={isOverflowing ? 'right-start' : 'bottom-start'}
			shouldUseCaptureOnOutsideClick={false}
			content={() => (
				<ContextualAnalyticsData sourceType={DROPDOWN} sourceName="navigationKitTabMenuOld">
					<Box xcss={containerStyles} testId="navigation-kit-tab-menu.ui.tab-menu-options">
						{canSetAsDefault && (
							<DropdownItemGroup>
								<SetAsDefaultMenuItem
									onClick={() => {
										handleSetAsDefault();
										handleMenuClose();
									}}
									onCompleted={onSetAsDefaultCompleted}
									tabFragment={tabData}
								/>
							</DropdownItemGroup>
						)}

						{position !== undefined && (
							<MoveItem
								hasSeparator={canSetAsDefault}
								id={tabData?.id}
								dataId={tabData?.__id}
								beforeItemId={beforeItemId}
								afterItemId={afterItemId}
								position={position}
								onClick={handleMenuClose}
								isOverflowing={isOverflowing}
							/>
						)}
						{canRemove && !tabData.isDefault && (
							<DropdownItemGroup
								hasSeparator={
									canSetAsDefault ||
									typeof beforeItemId === 'string' ||
									typeof afterItemId === 'string'
								}
							>
								<HideItem onClick={handleHideClick} />
							</DropdownItemGroup>
						)}
					</Box>
					<FireScreenAnalytics />
				</ContextualAnalyticsData>
			)}
		/>
	);
};

export default memo(TabMenu);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NavigationThemedButton = styled(ThemedButton)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	[Variables.COLOR_TEXT]: Tokens.COLOR_LINK,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	[Variables.COLOR_TEXT_SELECTED]: Tokens.COLOR_LINK,
});

const containerStyles = xcss({
	minWidth: '124px',
});
